import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils';

const UpcomingEvents = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const events = [
    {
      id: 1,
      title: 'Virtual Bridal Expo 2024',
      date: '2024-04-15',
      time: '10:00 AM - 6:00 PM',
      location: 'Online Event',
      type: 'Expo',
      description: 'Discover the latest trends in South Asian weddings with top vendors',
      image: 'https://images.unsplash.com/photo-1519167758481-83f29d8ae8c4?w=400&h=250&fit=crop',
      discount: '20% OFF',
      featured: true
    },
    {
      id: 2,
      title: 'Mehendi Art Workshop',
      date: '2024-04-20',
      time: '2:00 PM - 5:00 PM',
      location: 'San Francisco, CA',
      type: 'Workshop',
      description: 'Learn traditional mehendi patterns from master artists',
      image: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=400&h=250&fit=crop',
      discount: 'FREE',
      featured: false
    },
    {
      id: 3,
      title: 'Spring Wedding Showcase',
      date: '2024-04-25',
      time: '11:00 AM - 7:00 PM',
      location: 'Bay Area Convention Center',
      type: 'Showcase',
      description: 'Meet 50+ wedding vendors in one location',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=250&fit=crop',
      discount: 'Early Bird 30% OFF',
      featured: true
    },
    {
      id: 4,
      title: 'Bollywood Dance Classes',
      date: '2024-05-01',
      time: '7:00 PM - 9:00 PM',
      location: 'San Francisco Dance Studio',
      type: 'Class',
      description: 'Perfect your first dance with professional choreography',
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=250&fit=crop',
      discount: '15% OFF',
      featured: false
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % events.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + events.length) % events.length);
  };

  const handleRegister = (eventId) => {
    navigate('/event-detail', { state: { eventId } });
  };

  const handleLearnMore = (eventId) => {
    navigate('/event-detail', { state: { eventId } });
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'Expo': return 'bg-purple-500';
      case 'Workshop': return 'bg-blue-500';
      case 'Showcase': return 'bg-green-500';
      case 'Class': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-soft p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            Upcoming Events & Promotions
          </h3>
          <p className="text-text-secondary text-sm">
            Don't miss these wedding planning opportunities
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronLeft"
            onClick={prevSlide}
          />
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronRight"
            onClick={nextSlide}
          />
        </div>
      </div>

      <div className="relative overflow-hidden">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {events.map((event) => (
            <div key={event.id} className="w-full flex-shrink-0">
              <div className={`relative rounded-lg overflow-hidden ${
                event.featured ? 'bg-gradient-to-r from-primary-50 to-secondary-50' : 'bg-surface-50'
              }`}>
                <div className="md:flex">
                  <div className="md:w-1/2">
                    <div className="relative">
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-full h-48 md:h-64 object-cover"
                      />
                      <div className="absolute top-4 left-4 flex items-center space-x-2">
                        <div className={`${getEventTypeColor(event.type)} text-white px-3 py-1 rounded-full text-sm font-medium`}>
                          {event.type}
                        </div>
                        {event.featured && (
                          <div className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                            <Icon name="Star" size={14} className="inline mr-1" />
                            Featured
                          </div>
                        )}
                      </div>
                      <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        {event.discount}
                      </div>
                    </div>
                  </div>
                  <div className="md:w-1/2 p-6">
                    <h4 className="font-heading font-semibold text-xl text-text-primary mb-3">
                      {event.title}
                    </h4>
                    <p className="text-text-secondary mb-4 line-clamp-2">
                      {event.description}
                    </p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center space-x-2 text-sm text-text-muted">
                        <Icon name="Calendar" size={16} />
                        <span>{formatDate(event.date)}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-text-muted">
                        <Icon name="Clock" size={16} />
                        <span>{event.time}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-text-muted">
                        <Icon name="MapPin" size={16} />
                        <span>{event.location}</span>
                      </div>
                    </div>

                    <div className="flex space-x-3">
                      <Button
                        variant="primary"
                        size="sm"
                        iconName="UserPlus"
                        onClick={() => handleRegister(event.id)}
                      >
                        Register
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Info"
                        onClick={() => handleLearnMore(event.id)}
                      >
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="flex justify-center mt-6 space-x-2">
        {events.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              index === currentSlide ? 'bg-primary' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      <div className="mt-6 text-center">
        <Button
          variant="outline"
          iconName="Calendar"
          onClick={() => navigate('/planning-tools')}
        >
          Browse All Events
        </Button>
      </div>
    </div>
  );
};

export default UpcomingEvents;